import React, { useEffect, useState, useCallback } from 'react';
import './AdminOverview.css';
import './AdminActivity.css';
import StatTile from './StatTile';
import DauChart from './charts/DauChart';
import EventsByDayChart from './charts/EventsByDayChart';
import TopPagesChart from './charts/TopPagesChart';
import FunnelBars from './FunnelBars';
import RefreshButton from './RefreshButton';
import { adminApi } from './utils/adminApi';
import { formatCurrency, formatDateTime } from './utils/format';
import { countryCodeToFlagEmoji } from './utils/countryFlag';

const ONLINE_REFRESH_MS = 30 * 1000;

const formatActive = (ms) => {
  if (!ms || ms <= 0) return '0m';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const timeAgo = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '-';
  const secs = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const ERROR_LABELS = {
  js_error: 'JS error',
  api_error: 'API error',
  checkout_failed: 'Checkout failed',
  payment_failed: 'Payment failed',
};

const errorSummary = (event) => {
  const meta = event.meta || {};
  if (event.type === 'api_error') return `${meta.method || ''} ${meta.endpoint || ''} → ${meta.status ?? '?'}`.trim();
  if (event.type === 'js_error') return meta.message || '-';
  if (event.type === 'checkout_failed') return meta.reason || '-';
  if (event.type === 'payment_failed') return `${meta.status || 'failed'} (invoice ${meta.invoiceId || '?'})`;
  return '-';
};

// One anonymous visitor row: summary line, expandable to full detail +
// that visitor's event timeline. Its refresh button re-pulls JUST this
// visitor (and their events when expanded), not the whole list.
const VisitorRow = ({ visitor: visitorProp, onSelectUser }) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const [fresh, setFresh] = useState(null);
  const visitor = fresh || visitorProp;

  const loadEvents = () =>
    adminApi.getActivityEvents({ visitorId: visitor.id, limit: 25 })
      .then((res) => setEvents(res.events || []))
      .catch((err) => {
        console.error('Failed to load visitor events:', err);
        setEvents([]);
      });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) loadEvents();
  };

  const refreshOne = async () => {
    const res = await adminApi.getVisitor(visitor.id);
    if (res.visitor) setFresh(res.visitor);
    if (open) await loadEvents();
  };

  const geoLabel = visitor.geo
    ? `${visitor.geo.countryCode ? `${countryCodeToFlagEmoji(visitor.geo.countryCode)} ` : ''}${[visitor.geo.city, visitor.geo.country].filter(Boolean).join(', ') || 'Unknown'}`
    : 'Unknown';
  const deviceLabel = visitor.device
    ? [visitor.device.browser, visitor.device.os].filter(Boolean).join(' · ') || 'Unknown device'
    : 'Unknown device';
  const source = visitor.ref
    ? `ref: ${visitor.ref}`
    : visitor.utmSource
      ? `utm: ${visitor.utmSource}`
      : visitor.referrer
        ? (() => { try { return new URL(visitor.referrer).hostname; } catch { return 'link'; } })()
        : 'direct';

  return (
    <div className={`visitor-row ${open ? 'visitor-row-open' : ''}`}>
      <div className="visitor-row-main" onClick={toggle}>
        <span className="visitor-geo">{geoLabel}</span>
        <span className="visitor-device">
          {deviceLabel}
          {visitor.device?.deviceType && visitor.device.deviceType !== 'desktop' && (
            <span className="visitor-device-type"> ({visitor.device.deviceType})</span>
          )}
        </span>
        <span className="visitor-pages">{visitor.pageViews} views · {formatActive(visitor.totalActiveMs)}</span>
        <span className="visitor-source">{source}</span>
        <span className="visitor-badges">
          {visitor.linkedUid && (
            <span className="admin-badge" style={{ color: '#22c55e' }}>signed up</span>
          )}
          {visitor.suspicious && <span className="admin-badge" style={{ color: '#ef4444' }}>suspicious</span>}
          {visitor.excluded && <span className="admin-badge" style={{ color: '#686868' }}>excluded</span>}
        </span>
        <span className="visitor-seen">{timeAgo(visitor.lastSeenAt)}</span>
        <RefreshButton onRefresh={refreshOne} title="Refresh this visitor" />
      </div>

      {open && (
        <div className="visitor-detail">
          <div className="visitor-detail-grid">
            <div><span>Visitor ID</span><span className="admin-mono-sm">{visitor.id}</span></div>
            <div><span>First seen</span><span>{formatDateTime(visitor.firstSeenAt)}</span></div>
            <div><span>Last seen</span><span>{formatDateTime(visitor.lastSeenAt)}</span></div>
            <div><span>Landing page</span><span>{visitor.landingPage || '—'}</span></div>
            <div><span>Last page</span><span>{visitor.lastPage || '—'}</span></div>
            <div><span>Sessions</span><span>{visitor.sessionCount}</span></div>
            <div><span>Errors</span><span>{visitor.errorCount}</span></div>
            <div><span>Referrer</span><span className="admin-mono-sm">{visitor.referrer || '—'}</span></div>
            <div><span>Ref code</span><span>{visitor.ref || '—'}</span></div>
            <div><span>UTM</span><span>{[visitor.utmSource, visitor.utmMedium, visitor.utmCampaign].filter(Boolean).join(' / ') || '—'}</span></div>
            <div><span>IP</span><span className="admin-mono-sm">{visitor.geo?.ip || '—'}</span></div>
            <div><span>Primary fp</span><span className="admin-mono-sm">{visitor.primaryFingerprint ? `${visitor.primaryFingerprint.slice(0, 16)}…` : '—'}</span></div>
            <div><span>Secondary fp</span><span className="admin-mono-sm">{visitor.secondaryFingerprint ? `${visitor.secondaryFingerprint.slice(0, 16)}…` : '—'}</span></div>
            <div>
              <span>Account</span>
              <span>
                {visitor.linkedUid ? (
                  <button className="activity-user-link" onClick={() => onSelectUser && onSelectUser(visitor.linkedUid)}>
                    {visitor.linkedEmail || visitor.linkedUid}
                  </button>
                ) : 'anonymous'}
              </span>
            </div>
          </div>

          <div className="visitor-events">
            {events === null ? (
              <div className="no-data">Loading events…</div>
            ) : events.length === 0 ? (
              <div className="no-data">No events (older than 90 days or deleted)</div>
            ) : (
              events.map((e) => (
                <div key={e.id} className="visitor-event-row">
                  <span className="visitor-event-time">{formatDateTime(e.ts)}</span>
                  <span className="visitor-event-type">{e.type}</span>
                  <span className="admin-mono-sm">{e.page}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminActivity = ({ onSelectUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [online, setOnline] = useState({ users: [], userCount: 0, visitorCount: 0 });
  const [overview, setOverview] = useState({ days: [], topPages: [], eventTotals: {} });
  const [funnel, setFunnel] = useState([]);
  const [errors, setErrors] = useState([]);
  const [visitorsOpen, setVisitorsOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [visitorsCursor, setVisitorsCursor] = useState(null);
  const [visitorsHasMore, setVisitorsHasMore] = useState(false);
  const [visitorsLoading, setVisitorsLoading] = useState(false);

  const loadVisitors = useCallback(async (cursor = null) => {
    setVisitorsLoading(true);
    try {
      const res = await adminApi.getVisitors({ limit: 30, cursor: cursor || undefined });
      setVisitors((prev) => (cursor ? [...prev, ...(res.visitors || [])] : (res.visitors || [])));
      setVisitorsCursor(res.nextCursor);
      setVisitorsHasMore(Boolean(res.hasMore));
    } catch (err) {
      console.error('Failed to load visitors:', err);
    } finally {
      setVisitorsLoading(false);
    }
  }, []);

  const toggleVisitors = () => {
    const next = !visitorsOpen;
    setVisitorsOpen(next);
    if (next && visitors.length === 0) loadVisitors();
  };

  const loadOnline = useCallback(async () => {
    try {
      const data = await adminApi.getActivityOnline();
      setOnline(data);
    } catch (err) {
      console.error('Failed to load online users:', err);
    }
  }, []);

  const loadErrors = useCallback(async () => {
    const res = await adminApi.getActivityErrors({ limit: 50 });
    setErrors(res.errors || []);
  }, []);

  // soft=true refreshes in place without blanking the tab to "Loading..."
  const loadPeriodData = useCallback(async (numDays, soft = false) => {
    if (!soft) setLoading(true);
    setError('');
    try {
      const [overviewRes, funnelRes, errorsRes, onlineRes] = await Promise.all([
        adminApi.getActivityOverview(numDays),
        adminApi.getActivityFunnel(numDays),
        adminApi.getActivityErrors({ limit: 50 }),
        adminApi.getActivityOnline(),
      ]);
      setOverview(overviewRes);
      setFunnel(funnelRes.funnel || []);
      setErrors(errorsRes.errors || []);
      setOnline(onlineRes);
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setError(err.message || 'Failed to load activity data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPeriodData(days);
  }, [days, loadPeriodData]);

  useEffect(() => {
    const interval = setInterval(loadOnline, ONLINE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadOnline]);

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  const totals = (overview.days || []).reduce(
    (acc, d) => ({
      pageViews: acc.pageViews + (d.pageViews || 0),
      sessions: acc.sessions + (d.sessions || 0),
      errors: acc.errors + (d.errors || 0),
      signups: acc.signups + (d.signups || 0),
      revenue: acc.revenue + (d.revenueUsd || 0),
    }),
    { pageViews: 0, sessions: 0, errors: 0, signups: 0, revenue: 0 }
  );

  return (
    <div>
      <div className="activity-toolbar">
        <div className="activity-range-buttons">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              className={`admin-tab ${days === n ? 'active' : ''}`}
              onClick={() => setDays(n)}
            >
              {n}d
            </button>
          ))}
        </div>
        <RefreshButton onRefresh={() => loadPeriodData(days, true)} title="Refresh all activity data" />
      </div>

      <div className="admin-stats-grid">
        <StatTile label="Online Now" value={online.userCount} accent="#22c55e" />
        <StatTile label="Active Visitors" value={online.visitorCount} accent="#9085e9" onClick={toggleVisitors} />
        <StatTile label={`Page Views (${days}d)`} value={totals.pageViews} />
        <StatTile label={`Sessions (${days}d)`} value={totals.sessions} />
        <StatTile label={`Signups (${days}d)`} value={totals.signups} />
        <StatTile label={`Errors (${days}d)`} value={totals.errors} accent={totals.errors > 0 ? '#ef4444' : undefined} />
        <StatTile label={`Revenue (${days}d)`} value={formatCurrency(totals.revenue)} />
      </div>

      <div className="admin-section">
        <div className="admin-block-head">
          <h2>Online Users <span className="activity-live-dot" /></h2>
          <RefreshButton onRefresh={loadOnline} title="Refresh online users" />
        </div>
        <div className="online-users-list">
          {online.users.length === 0 ? (
            <div className="no-data">No users online right now</div>
          ) : (
            online.users.map((u) => (
              <div
                key={u.id}
                className="online-user-row"
                onClick={() => onSelectUser && onSelectUser(u.id)}
              >
                <span className="online-user-email">{u.email || u.id}</span>
                <span className="online-user-page">{u.lastPage || '-'}</span>
                <span className="online-user-plan">{u.planId || 'no plan'}</span>
                <span className="online-user-seen">{timeAgo(u.lastActivity)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="admin-section">
        <div className="visitors-section-head">
          <h2>Anonymous Visitors</h2>
          <div className="admin-head-actions">
            {visitorsOpen && <RefreshButton onRefresh={() => loadVisitors()} title="Refresh visitor list" />}
            <button className="admin-btn admin-btn-secondary" onClick={toggleVisitors}>
              {visitorsOpen ? 'Hide' : 'Show visitors'}
            </button>
          </div>
        </div>
        {visitorsOpen && (
          <>
            <div className="visitors-list">
              {visitors.length === 0 && !visitorsLoading ? (
                <div className="no-data">No visitors recorded yet</div>
              ) : (
                visitors.map((v) => (
                  <VisitorRow key={v.id} visitor={v} onSelectUser={onSelectUser} />
                ))
              )}
            </div>
            {visitorsLoading && <div className="admin-loading">Loading…</div>}
            {visitorsHasMore && !visitorsLoading && (
              <button className="admin-btn admin-btn-secondary visitors-more" onClick={() => loadVisitors(visitorsCursor)}>
                Load more
              </button>
            )}
          </>
        )}
      </div>

      <div className="admin-charts-grid">
        <div className="admin-chart-card">
          <h3>Daily Active (users vs visitors)</h3>
          <DauChart data={overview.days} />
        </div>
        <div className="admin-chart-card">
          <h3>Activity by Day</h3>
          <EventsByDayChart data={overview.days} />
        </div>
        <div className="admin-chart-card">
          <h3>Top Pages</h3>
          <TopPagesChart data={overview.topPages} />
        </div>
        <div className="admin-chart-card">
          <h3>Conversion Funnel ({days}d)</h3>
          <FunnelBars funnel={funnel} />
        </div>
      </div>

      <div className="admin-section">
        <div className="admin-block-head">
          <h2>Recent Errors</h2>
          <RefreshButton onRefresh={loadErrors} title="Refresh errors" />
        </div>
        <div className="activity-errors-table-wrap">
          {errors.length === 0 ? (
            <div className="no-data">No errors recorded — all good</div>
          ) : (
            <table className="activity-errors-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Page</th>
                  <th>Detail</th>
                  <th>Who</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDateTime(e.ts)}</td>
                    <td>
                      <span className="admin-badge" style={{ color: '#ef4444' }}>
                        {ERROR_LABELS[e.type] || e.type}
                      </span>
                    </td>
                    <td>{e.page || '-'}</td>
                    <td className="activity-error-detail">{errorSummary(e)}</td>
                    <td>
                      {e.uid ? (
                        <button
                          className="activity-user-link"
                          onClick={() => onSelectUser && onSelectUser(e.uid)}
                        >
                          user
                        </button>
                      ) : (
                        <span className="activity-anon">visitor</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActivity;
